import { Box, useMediaQuery } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import React, { useEffect } from "react"
import { useAppContext, useAppDispatchContext } from "../../app/Context"
import { getViewWidth } from "../../constants/styles"
import { useStorage } from "../../hooks"
import { Agent } from "../../types/Agent"
import { StateAction } from "../../types/State"
import { JoinMeetingModal } from "../Modal/JoinMeeting"
import Overlay from "../Modal/Overlay"
import { UserLoginModal } from "../Modal/UserLogin"
import { AgentVideo } from "./AgentVideo"
import { Bottom } from "./Bottom"

interface AgentViewProps {
  agent: Agent
  permissionsGranted: boolean
  isUserAnonymous: boolean
  setPermissionsGranted: (granted: boolean) => void
  onJoinCall: () => void
  joinMeetingModalProps?: { open: boolean; onClose: () => void }
  loginModalProps?: { open: boolean; onOpen?: () => void; onClose: () => void }
  isEmbed: boolean
  agentExists: boolean
}

export const AgentView: React.FC<AgentViewProps> = ({
  agent,
  permissionsGranted,
  isUserAnonymous,
  setPermissionsGranted,
  joinMeetingModalProps,
  loginModalProps,
  onJoinCall,
  isEmbed,
  agentExists,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const dispatch = useAppDispatchContext()
  const context = useAppContext()
  const { getItem } = useStorage()

  const { stream, user } = context || {}

  useEffect(() => {
    // Only clean up stream if we have one
    if (stream) {
      dispatch({ type: StateAction.setStream, payload: null })
    }
  }, [location.pathname, location.search])

  const handleGrantPermissions = (granted: boolean) => {
    if (granted) {
      setPermissionsGranted(granted)
      if (isUserAnonymous && loginModalProps?.onOpen) {
        loginModalProps.onOpen()
      }
    }
  }

  const handleResetPermissions = () => {
    setPermissionsGranted(false)
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      dispatch({ type: StateAction.setStream, payload: null })
    }
  }

  const handleCloseJoinModal = () => {
    handleResetPermissions()
    joinMeetingModalProps?.onClose()
  }

  const handleCloseLoginModal = () => {
    handleResetPermissions()
    loginModalProps?.onClose()
  }

  return (
    <React.Fragment>
      <Box
        height={"100%"}
        minHeight={"500px"}
        width={"100%"}
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        justifyContent={"flex-start"}
        position={"relative"}
        sx={{
          padding: isEmbed ? 0 : { xs: "5px", sm: "5px 10px 10px 10px" },
          overflow: "auto",
        }}
      >
        {isMobile ? (
          <Box
            display={"flex"}
            flexDirection={"column"}
            height={"100%"}
            width={"100%"}
          >
            <Box flex={1} position={"relative"}>
              <div
                className="lk-video-conference-inner"
                style={{
                  height: "100%",
                  width: "100%",
                  position: "relative",
                  minHeight: "400px",
                }}
              >
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  position={"relative"}
                  borderRadius={isEmbed ? 0 : "1rem"}
                  overflow={"hidden"}
                  width={"100%"}
                  height={"100%"}
                >
                  <AgentVideo agent={agent} isEmbed={isEmbed} />
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                  />
                  {!permissionsGranted && (
                    <Bottom
                      agent={agent}
                      handleGrantPermissions={handleGrantPermissions}
                      agentExists={agentExists}
                    />
                  )}
                  {joinMeetingModalProps && (
                    <JoinMeetingModal
                      open={joinMeetingModalProps.open}
                      onClose={handleCloseJoinModal}
                      onJoinCall={onJoinCall}
                    />
                  )}
                  <UserLoginModal
                    open={loginModalProps?.open}
                    isUserAnonymous={isUserAnonymous}
                    onClose={loginModalProps?.onClose}
                    onJoinCall={onJoinCall}
                    onResetFlow={handleCloseLoginModal}
                  />
                </Box>
              </div>
            </Box>
          </Box>
        ) : (
          <div
            className="lk-video-conference-inner"
            style={{
              width: getViewWidth(isEmbed),
              marginRight: "unset",
              margin: isEmbed ? "0px" : "0px auto",
              height: "100%",
              minHeight: "500px",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            <Box
              display={"flex"}
              flexDirection={"column"}
              position={"relative"}
              borderRadius={isEmbed ? 0 : "1rem"}
              overflow={"hidden"}
              width={"100%"}
              height={"100%"}
            >
              <AgentVideo agent={agent} isEmbed={isEmbed} />
              <Box
                position="absolute"
                top={0}
                left={0}
                width="100%"
                height="100%"
              />
              {!permissionsGranted && (
                <Bottom
                  agent={agent}
                  handleGrantPermissions={handleGrantPermissions}
                  agentExists={agentExists}
                />
              )}
              {joinMeetingModalProps && (
                <JoinMeetingModal
                  open={joinMeetingModalProps.open}
                  onClose={handleCloseJoinModal}
                  onJoinCall={onJoinCall}
                />
              )}
              <UserLoginModal
                open={loginModalProps?.open}
                isUserAnonymous={isUserAnonymous}
                onClose={loginModalProps?.onClose}
                onJoinCall={onJoinCall}
                onResetFlow={handleCloseLoginModal}
              />
            </Box>
          </div>
        )}
        <Overlay
          open={loginModalProps?.open || joinMeetingModalProps?.open || false}
        />
      </Box>
    </React.Fragment>
  )
}
